import React, {
 useState,
 useEffect,
 useCallback,
} from 'react';

import { useSearchParams } from 'react-router-dom';

import ForumHeader from '../../features/forum/components/ForumHeader';
import ForumFilters from '../../features/forum/components/ForumFilters';
import ForumGrid from '../../features/forum/components/ForumGrid';
import DossierForm from '../../features/forum/components/DossierForm';

import PageAnimator from '../../components/ui/PageAnimator';

import {
 getDossiers,
 createDossier,
} from '../../features/forum/api/forumAPI';

import { getForumKPIs } from '../../features/forum/api/kpiAPI';

import {
 FileSearch,
 BarChart3,
 CheckCircle,
 XCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNotification } from '../../context/NotificationContext';

export default function Forum() {
  const { showToast } = useNotification();
 const [searchParams, setSearchParams] = useSearchParams();

 const [dossiers, setDossiers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [loadingProgress, setLoadingProgress] = useState(0);
 const [isFormOpen, setIsFormOpen] = useState(false);
 const [kpis, setKpis] = useState(null);

 const [filters, setFilters] = useState(() => ({
 status: searchParams.get('status') || '',
 type: searchParams.get('type') || '',
 search: searchParams.get('search') || '',
 }));

 useEffect(() => {
 const nextParams = new URLSearchParams();

 if (filters.status) {
 nextParams.set('status', filters.status);
 }

 if (filters.type) {
 nextParams.set('type', filters.type);
 }

 if (filters.search) {
 nextParams.set('search', filters.search);
 }

 const currentQueryString = searchParams.toString();
 const nextQueryString = nextParams.toString();

 if (currentQueryString !== nextQueryString) {
 setSearchParams(nextParams, {
 replace: true,
 });
 }
 }, [
 filters.status,
 filters.type,
 filters.search,
 searchParams,
 setSearchParams,
 ]);

 useEffect(() => {
 let isCurrentRequest = true;

 const loadDossiers = async () => {
 setLoading(true);

 try {
 const minWait = new Promise(resolve => setTimeout(resolve, 2000));
 const [data] = await Promise.all([
 getDossiers({
 status: filters.status,
 type: filters.type,
 search: filters.search,
 }, {
 onDownloadProgress: (progressEvent) => {
 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
 setLoadingProgress(percentCompleted);
 }
 }),
 minWait
 ]);

 if (isCurrentRequest) {
 setDossiers(Array.isArray(data) ? data : []);
 }
 } catch (error) {
 if (isCurrentRequest) {
 console.error(
 'Erreur lors du chargement des dossiers',
 error,
 );
 }
 } finally {
 if (isCurrentRequest) {
 setLoading(false);
 }
 }
 };

 loadDossiers();

 return () => {
 isCurrentRequest = false;
 };
 }, [
 filters.status,
 filters.type,
 filters.search,
 ]);

 const fetchKpis = useCallback(async (start = null, end = null) => {
 try {
 const data = await getForumKPIs(start, end);
 setKpis(data);
 } catch (error) {
 console.error('Erreur lors du chargement des KPI', error);
 }
 }, []);

 const handleDateFilter = useCallback(
 (start, end) => {
 fetchKpis(start, end);
 },
 [fetchKpis],
 );

 const refreshDossiers = useCallback(async () => {
 setLoading(true);

 try {
 const minWait = new Promise(resolve => setTimeout(resolve, 2000));
 const [data] = await Promise.all([
 getDossiers({
 status: filters.status,
 type: filters.type,
 search: filters.search,
 }, {
 onDownloadProgress: (progressEvent) => {
 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
 setLoadingProgress(percentCompleted);
 }
 }),
 minWait
 ]);

 setDossiers(Array.isArray(data) ? data : []);
 } catch (error) {
 console.error(
 'Erreur lors du rechargement des dossiers',
 error,
 );
 } finally {
 setLoading(false);
 }
 }, [
 filters.status,
 filters.type,
 filters.search,
 ]);

 const handleCreateDossier = useCallback(
  async (formData) => {
  try {
  await createDossier(formData);
  showToast('Dossier créé avec succès', 'success');

  setIsFormOpen(false);
  await refreshDossiers();
  } catch (error) {
  console.error(
  'Erreur lors de la création du dossier',
  error,
  );

  showToast('Erreur lors de la création du dossier', 'error');
  }
  },
  [refreshDossiers, showToast],
  );

 // 🔹 Variantes d'animation pour les KPI
 const kpiContainerVariants = {
 hidden: { opacity: 0 },
 show: {
 opacity: 1,
 transition: { staggerChildren: 0.1 }
 }
 };

 const kpiItemVariants = {
 hidden: { opacity: 0, y: 15 },
 show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
 };

  return (
    <div className="p-4 sm:p-8 bg-workspace h-full">
      <PageAnimator>
        <div className="mb-6">
          <ForumHeader
            onNewDossier={() => setIsFormOpen(true)}
            filters={filters}
            setFilters={setFilters}
            handleDateFilter={handleDateFilter}
          />
        </div>

        {kpis && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
            variants={kpiContainerVariants}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={kpiItemVariants} className="workspace-card p-4 flex items-center">
              <div className="p-3 rounded-full bg-primary-light text-primary mr-4">
                <FileSearch className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-tertiary font-medium">Dossiers reçus</p>
                <p className="text-2xl font-bold text-primary">{kpis.totalDossiers ?? 0}</p>
              </div>
            </motion.div>

            <motion.div variants={kpiItemVariants} className="workspace-card p-4 flex items-center">
              <div className="p-3 rounded-full badge-success mr-4">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-tertiary font-medium">Validés / Convertis</p>
                <p className="text-2xl font-bold text-primary">{kpis.validatedDossiers ?? 0}</p>
              </div>
            </motion.div>

            <motion.div variants={kpiItemVariants} className="workspace-card p-4 flex items-center">
              <div className="p-3 rounded-full badge-info mr-4">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-tertiary font-medium">Taux de conversion</p>
                <p className="text-2xl font-bold text-primary">{kpis.conversionRate ?? 0} %</p>
              </div>
            </motion.div>

            <motion.div variants={kpiItemVariants} className="workspace-card p-4 flex items-center">
              <div className="p-3 rounded-full badge-danger mr-4">
                <XCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-tertiary font-medium">Refusés</p>
                <p className="text-2xl font-bold text-primary">{kpis.rejectedDossiers ?? 0}</p>
              </div>
            </motion.div>
          </motion.div>
        )}

        <div className="mb-6 workspace-card">
          <ForumFilters
            filters={filters}
            setFilters={setFilters}
          />
        </div>

        <ForumGrid 
          dossiers={dossiers} 
          loading={loading} 
          progress={loadingProgress} 
        />
      </PageAnimator>

      <DossierForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreateDossier}
      />
    </div>
  );
}
